import axios from "axios";

const LANG_FROM_BE = "gb";
const LANG_TO_FE = "en";

// Fields whose string *values* carry a language code (not just keys/suffixes)
const LANG_VALUE_FIELDS = new Set(["language", "input_language"]);

// Only walk plain objects: Date, File, Blob, FormData, moment etc. must pass through untouched
function isPlainObject(value) {
  if (Object.prototype.toString.call(value) !== "[object Object]") return false;
  const proto = Object.getPrototypeOf(value);
  return proto === null || proto === Object.prototype;
}

function transformLangKeys(data, from, to) {
  if (data === null || data === undefined) return data;

  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      if (typeof parsed === "object" && parsed !== null && !Array.isArray(parsed) && from in parsed) {
        const transformed = transformLangKeys(parsed, from, to);
        return JSON.stringify(transformed);
      }
    } catch (e) {
      // not JSON
    }
    return data;
  }

  if (Array.isArray(data)) {
    return data.map((item) => transformLangKeys(item, from, to));
  }

  if (isPlainObject(data)) {
    const result = {};
    const suffix = "_" + from;
    const newSuffix = "_" + to;
    for (const [key, value] of Object.entries(data)) {
      let newKey = key;
      if (key === from) {
        newKey = to;
      } else if (key.endsWith(suffix)) {
        newKey = key.slice(0, -suffix.length) + newSuffix;
      }
      // Transform string values of known language fields
      if (LANG_VALUE_FIELDS.has(key) && value === from) {
        result[newKey] = to;
      } else {
        result[newKey] = transformLangKeys(value, from, to);
      }
    }
    return result;
  }

  return data;
}

// Convert legacy BE-shaped data (gb keys) persisted client-side before the gb → en switch
export function migrateLegacyLangKeys(data) {
  return transformLangKeys(data, LANG_FROM_BE, LANG_TO_FE);
}

// Requests to third-party-bound proxies (e.g. Google geocoding) can opt out with
// { skipLangTransform: true } in the axios config, so "en" is not rewritten to "gb"
export function installLanguageInterceptors() {
  // Response: BE (gb) → FE (en)
  axios.interceptors.response.use((response) => {
    if (response.data) {
      response.data = transformLangKeys(response.data, LANG_FROM_BE, LANG_TO_FE);
    }
    return response;
  });

  // Request: FE (en) → BE (gb)
  axios.interceptors.request.use((config) => {
    if (config.skipLangTransform) return config;
    if (config.data && typeof config.data === "object") {
      config.data = transformLangKeys(config.data, LANG_TO_FE, LANG_FROM_BE);
    }
    if (config.params && typeof config.params === "object") {
      config.params = transformLangKeys(config.params, LANG_TO_FE, LANG_FROM_BE);
    }
    // Transform language values embedded in URL query strings (e.g. ?language=en → ?language=gb)
    if (config.url) {
      config.url = config.url.replace(
        /([?&]language=)en(?=&|$)/g,
        "$1" + LANG_FROM_BE
      );
    }
    return config;
  });
}
